import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import FloatingBulb from "@/components/FloatingBulb";
import Tagline from "@/components/Tagline";
import { Reveal } from "@/components/motion";
import PercheCards from "@/components/chisiamo/PercheCards";
import ValuesWheel from "@/components/chisiamo/ValuesWheel";
import MethodTimeline from "@/components/chisiamo/MethodTimeline";
import AudienceSection from "@/components/home/AudienceSection";
import { useSeo, SITE_URL } from "@/lib/seo";

const MANIFESTO = [
  "Ogni impresa ha bisogno di competenze diverse per crescere. Di solito questo significa spiegare la stessa storia a consulenti diversi, e mettere d'accordo pareri che non dialogano tra loro.",
  "Idea 360 Consulting nasce per essere l'eccezione. Il nostro team ha oltre 8 anni di esperienza in settori tra loro molto diversi — dalla ristorazione alla nautica, dall'edilizia al benessere — e questo ci dà una visione ampia su ciò che davvero fa crescere un'azienda, o fa partire con il piede giusto una startup che ha solo un'idea.",
  "Non siamo consulenti esterni che intervengono una volta e spariscono. Siamo un partner operativo e strategico: pensiamo la strategia insieme a te, e restiamo per eseguirla.",
];

export default function ChiSiamo() {
  useSeo({
    title: "Chi siamo: il partner operativo e strategico | Idea 360 Consulting",
    description:
      "Un team con oltre 8 anni di esperienza in settori diversi — ristorazione, nautica, edilizia, benessere — che affianca PMI, startup e privati in Campania: strategia ed esecuzione, un solo referente.",
    path: "/chi-siamo",
    jsonLd: {
      "@context": "https://schema.org",
      "@type": "AboutPage",
      name: "Chi siamo — Idea 360 Consulting",
      url: `${SITE_URL}/chi-siamo`,
      about: { "@id": `${SITE_URL}/#organization` },
      mainEntity: {
        "@id": `${SITE_URL}/#organization`,
        knowsAbout: [
          "consulenza aziendale", "finanza agevolata", "controllo di gestione", "business plan",
          "marketing strategico", "risorse umane", "consulenza del lavoro", "formazione finanziata", "contabilità",
        ],
      },
    },
  });

  return (
    <>
      {/* HERO + MANIFESTO */}
      <section className="relative">
        <FloatingBulb size={30} className="absolute top-28 right-[12%] hidden md:block" testId="chisiamo-bulb" />
        <div className="max-w-7xl mx-auto px-6 lg:px-8 pt-20 pb-24 sm:pt-28 sm:pb-32">
          <p className="eyebrow mb-5">Chi siamo</p>
          <h1
            className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white max-w-4xl leading-[1.05]"
            style={{ fontFamily: "'Outfit', sans-serif", letterSpacing: "-0.03em" }}
            data-testid="chisiamo-h1"
          >
            Il partner <span className="text-[#FACC15]">operativo e strategico</span> dietro ogni decisione
          </h1>
          <Tagline className="mt-8" size="text-base" testId="chisiamo-tagline" />

          <div className="mt-16 grid grid-cols-1 lg:grid-cols-12 gap-8" data-testid="chisiamo-manifesto">
            <div className="lg:col-span-4">
              <p className="eyebrow">Manifesto</p>
            </div>
            <div className="lg:col-span-8 space-y-6">
              {MANIFESTO.map((t, i) => (
                <Reveal as="p" key={i} delay={i * 0.12} className={`leading-relaxed ${i === 0 ? "text-white text-lg sm:text-xl" : "text-white/70 text-base sm:text-lg"}`}>
                  {t}
                </Reveal>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* PRINCIPI — blocco giallo */}
      <section className="bg-[#FACC15] text-black py-14 sm:py-28" data-testid="principi-section">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <p className="eyebrow-dark mb-4">I nostri valori</p>
          <h2
            className="text-3xl sm:text-4xl lg:text-5xl font-bold leading-[1.05] max-w-3xl"
            style={{ fontFamily: "'Outfit', sans-serif", letterSpacing: "-0.03em" }}
            data-testid="principi-h2"
          >
            Quattro principi che guidano ogni progetto
          </h2>
          <div className="mt-14">
            <ValuesWheel />
          </div>
        </div>
      </section>

      <PercheCards />
      <MethodTimeline />
      <AudienceSection eyebrow="Per chi lavoriamo" title={<>Per chi <span className="text-[#FACC15]">lavoriamo</span></>} />

      {/* CTA */}
      <section className="py-16 sm:py-32 relative overflow-hidden">
        <div
          className="absolute inset-0 opacity-[0.06] pointer-events-none"
          style={{ background: "radial-gradient(circle at 50% 50%, rgba(250,204,21,0.5) 0%, transparent 55%)" }}
          aria-hidden="true"
        />
        <div className="max-w-4xl mx-auto px-6 lg:px-8 text-center relative z-10">
          <h2
            className="text-3xl sm:text-4xl lg:text-5xl font-bold text-white mb-8"
            style={{ fontFamily: "'Outfit', sans-serif", letterSpacing: "-0.025em" }}
          >
            Lavoriamo insieme alla tua <span className="text-[#FACC15]">prossima mossa</span>
          </h2>
          <Link to="/contatti" className="btn-yellow inline-flex items-center gap-2" data-testid="chisiamo-cta">
            Richiedi una consulenza
            <ArrowRight size={18} />
          </Link>
        </div>
      </section>
    </>
  );
}
